/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Button, Card, Center, Loader } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { DismissalType } from '../../../../models/core/dismissals.type';
import dismissalsService from '../../../../services/core/dismissals.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: DismissalType | null;
};

function DismissalView() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const modals = useModals();
  const { idUsuarioDesligamento } = useParams();

  const [result, setResult] = useState<Result>({
    loading: location?.state?.dismissal === undefined,
    data: location?.state?.dismissal || null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: DismissalType | null = null;
      try {
        data = await dismissalsService.select({ idUsuarioDesligamento: Number(idUsuarioDesligamento) });
      } catch (error: any) {
        showNotification({
          title: 'Desligamento - Visualizar',
          message: error?.isBusinessException
            ? error.description
            : `Não foi possível carregar o desligamento.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idUsuarioDesligamento, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await dismissalsService.delete({ idUsuarioDesligamento: Number(idUsuarioDesligamento) });
      navigate('/dismissals');

      showNotification({
        title: 'Desligamento - Visualizar',
        message: `Desligamento excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Desligamento - Visualizar',
        message: error?.isBusinessException ? error.description : `Não foi possível excluir o desligamento.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir esse desligamento?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Managerial.Dismissals}
        title="Desligamento - Visualizar"
        description="Visualize os detalhes de um desligamento."
        buttons={[
          <Button
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={result.loading}
            loading={deleting}
          >
            Excluir
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
    </Card>
  );
}

export default DismissalView;
